import React, { useEffect, useState } from "react";
import { Constants, Deductions, isSameUkcRallyClass, isUkcRallyLevel, ukcRallyTableLevel } from "@eagerdog/constants";
import { apiService } from "src/services";
import { IShow, IShowElement, IShowElementExercise, IEventSummary } from "@eagerdog/interfaces";

import { helperService } from "src/services/helper.service";

import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import StopWatch, { convertTimeToSeconds, convertTimeToMinutes } from "../StopWatch/StopWatch";
import DeductionForm, { IDeduction } from "../DeductionForm/DeductionForm";
import Input from "src/components/Input/Input";
import Checkbox from "src/components/Checkbox/Checkbox";

import "./RallyObedienceResults.scss";

interface IProps {
  result: any,
  onChange(result: any, show?: IShow): void,
  event: IEventSummary
  onExerciseChange(): void
}

const RallyObedienceResults: React.FC<IProps> = (props) => {
  const [currentShow, setCurrentShow] = useState<IShow>();

  // Show element for the result's class. UKC Rally RO1..ROM compare level + section so a
  // class saved as "RO1 B" and one saved as RO1 + B resolve to the same element.
  const isResultShowElement = (se: IShowElement): boolean =>
    isUkcRallyLevel(props.result.dog_class.level) && isUkcRallyLevel(se.level)
      ? isSameUkcRallyClass(se, props.result.dog_class)
      : se.level === props.result.dog_class.level;

  const getOptionLabel = (id: string) => {
    switch (id) {
      case Constants.score_result_type.qualified:
        return "Qualified";
      case Constants.score_result_type.not_qualified:
        return "Not Qualified";
      case Constants.score_result_type.non_regular:
        return "Non-Regular";
      case Constants.score_result_type.disqualified:
        return "Disqualified";
      case Constants.score_result_type.excused:
        return "Excused";
      case Constants.score_result_type.absent:
        return "Absent";
      case Constants.score_result_type.bitch_in_season:
        return "Bitch in Season";
      case Constants.score_result_type.scratched:
        return "Scratched";
      case Constants.score_result_type.conflict:
        return "Conflict";
    }

    return "None";
  }

  const [totalsOnly, setTotalsOnly] = useState<boolean>(props.result.rally_obedience?.total_deductions_only !== undefined ? props.result.rally_obedience?.total_deductions_only : true);
  const [totalAmt, setTotalAmt] = useState<string>(props.result.rally_obedience?.total_deductions ? props.result.rally_obedience?.total_deductions : "0");
  const [excluded, setExcluded] = useState<boolean>(props.result.rally_obedience?.excluded_from_placement || false);

  // Non-regular (200-point) classes: Pairs and Teams variants
  const NON_REGULAR_LEVELS = [
    Constants.dog_class_element_level.pairs,
    Constants.dog_class_element_level.novice_pairs,
    Constants.dog_class_element_level.advanced_pairs,
    Constants.dog_class_element_level.excellent_pairs,
    Constants.dog_class_element_level.team,
    Constants.dog_class_element_level.novice_teams,
    Constants.dog_class_element_level.advanced_teams,
  ];
  // Check if this is a non-regular (Pairs/Teams) class — max score 200
  const isPairsClass = NON_REGULAR_LEVELS.includes(props.result.dog_class?.level);

  const [deductions, setDeductions] = useState<IDeduction[]>([]);
  const [result, setResult] = useState<IOption>({ 
    id: isPairsClass ? Constants.score_result_type.non_regular : (props.result.rally_obedience?.result_type ? props.result.rally_obedience?.result_type : Constants.score_result_type.qualified),
    value: isPairsClass ? "Non-Regular" : (props.result.rally_obedience?.result_type ? getOptionLabel(props.result.rally_obedience?.result_type) : "Qualified")
  });
  const [time, setTime] = useState<string>(props.result.rally_obedience?.time ? convertTimeToMinutes(props.result.rally_obedience?.time.toString()) : "");

  const resultOptions:IOption[] = [
    { id: "None", value: "None" },
    { id: Constants.score_result_type.qualified, value: "Qualified" },
    { id: Constants.score_result_type.not_qualified, value: "Not Qualified" },
    { id: Constants.score_result_type.non_regular, value: "Non-Regular" },
    { id: Constants.score_result_type.disqualified, value: "Disqualified" },
    { id: Constants.score_result_type.excused, value: "Excused" },
    { id: Constants.score_result_type.absent, value: "Absent" },
    { id: Constants.score_result_type.bitch_in_season, value: "Bitch in Season" }
  ]

  const pResult = helperService.usePrevious(result);
  const pDeductions = helperService.usePrevious(deductions);
  const pTime = helperService.usePrevious(time);

  const [loaded, setLoaded] = useState<boolean>(false);

  useEffect(() => {
    if (!loaded) {
      const getExerciseOrder = (showElement:IShowElement | string) => {
        return new Promise((resolve) => {
          let hasExerciseOrder:string[] = [
            Constants.show_type.obedience,
            Constants.show_type.rally_obedience
          ];

          if (typeof showElement === "string") {
            resolve("I");
          } else if (hasExerciseOrder.includes(props.result.show_type)) {
            if (showElement.order_of_exercises !== undefined) {
              resolve(showElement.order_of_exercises);
            } else {
              resolve("I");
            }
          } else {
            resolve("I");
          }
        });
      }

      const getShow = () => {
        return new Promise<IShow|string>((resolve) => {
          // Try without any query filters first, then filter on frontend
          apiService.getClubEventShows(props.result.club_id, props.result.event_id).then((response) => {
            if (response.length > 0) {
              // Filter to find the specific show by ID
              const targetShow = response.find((show: IShow) => show._id === props.result.show_id);
              if (targetShow) {
                resolve(targetShow);
              } else {
                resolve("NO_SHOW");
              }
            } else {
              resolve("NO_SHOW");
            }
          }).catch((error) => {
            resolve("NO_SHOW");
          });
        });
      }

      const getShowElement = (show: IShow) => {
        return new Promise<IShowElement | string>((resolve) => {
            if (show.show_elements) {
              for (let se in show.show_elements) {
                if (isResultShowElement(show.show_elements[se])) {
                  resolve(show.show_elements[se]);
                }
              }
            }

            resolve("NO_SHOW_ELEMENT");
        });
      }

      const getDeductionAmount = (currentDeduction: string) => {
        let hasDeduction = props.result.rally_obedience?.element_deductions?.filter((d: any) => { return d.element_name === currentDeduction; });

        if (hasDeduction?.length > 0) {
          return hasDeduction[0].element_deductions.toString();
        }

        return "0";
      }

      const loadDetailedDeductions = (exerciseOrder: string) => {
        let _deductions:IDeduction[] = [];

        if (exerciseOrder !== undefined) {
          // UKC Rally tables are keyed by level; "RO1 B" and canonical "RO1" both resolve to RO1
          let _deductionDef:any[] = Deductions[props.result.sanctioning_club][props.result.show_type][ukcRallyTableLevel(props.result.dog_class.level)][exerciseOrder];

          if (_deductionDef?.length > 0) {
            for (let d in _deductionDef) {
              _deductions.push({
                label: _deductionDef[d].exercise ? _deductionDef[d].exercise : _deductionDef[d],
                id: _deductionDef[d].exercise ? _deductionDef[d].exercise.toLowerCase().replaceAll(" ", "_") : _deductionDef[d].toLowerCase().replaceAll(" ", "_"),
                amount: props.result.rally_obedience !== undefined ? getDeductionAmount(_deductionDef[d].exercise ? _deductionDef[d].exercise : _deductionDef[d]) : "0",
                disabled: false,
                sign: _deductionDef[d].sign
              });
            }
          }
        }

        return _deductions;
      }

      const updateShowExercises = (deductions: IDeduction[], show: IShow, showElement: IShowElement | string) => {
        let _show:any = JSON.parse(JSON.stringify(show));

        if (typeof showElement !== "string") {
          let deductionPayload:any = deductions.map((d: IDeduction, index: number) => {
            return {
              sign: d.sign,
              exercise: d.label,
              disabled: d.disabled
            };
          });

          for (let se in _show.show_elements) {
            if (isResultShowElement(_show.show_elements[se])) {
              _show.show_elements[se].exercises = deductionPayload;
              break;
            }
          }

          setCurrentShow(show);
        }
      }

      const loadExercises = async() => {
        const show:IShow | string = await getShow();

        if (typeof show !== "string") {
          setCurrentShow(show);
          const showElement:IShowElement | string = await getShowElement(show);
          const exerciseOrder:any = await getExerciseOrder(showElement);

          if (typeof showElement !== "string" && showElement.exercises !== undefined && showElement.exercises.length > 0) {
            let _deductions:IDeduction[] = showElement.exercises.map((exercise: IShowElementExercise, index: number) => {
              return {
                label: exercise.exercise,
                id: exercise.exercise.toLowerCase().replaceAll(" ", "_"),
                amount: props.result.rally_obedience !== undefined ? getDeductionAmount(exercise.exercise) : "0",
                disabled: exercise.disabled || false,
                sign: exercise.sign
              };
            });

            setDeductions(_deductions);
          } else {
            if (exerciseOrder !== undefined) {
              let _deductions:IDeduction[] = loadDetailedDeductions(exerciseOrder);
              setDeductions(_deductions);
              updateShowExercises(_deductions, show, showElement);
            }
          }
        } else {
          // Show failed to load
        }
        
        // Always set loaded to true regardless of success/failure
        setLoaded(true);
      }

      loadExercises();
    }
  }, [loaded, props.result.club_id, props.result.dog_class_level, props.result.event_id, props.result.show_id, props.result.dog_class.level, props.result.show_type, props.result.rally_obedience, props.result.sanctioning_club]);

  let { onChange } = props;

  const pTotalAmt = helperService.usePrevious(totalAmt);
  const pTotalsOnly = helperService.usePrevious(totalsOnly);
  const pExcluded = helperService.usePrevious(excluded);

  // Check if current result is non-qualifying (doesn't need time/deductions)
  const isNonQualifyingResult = [
    Constants.score_result_type.not_qualified,
    Constants.score_result_type.absent,
    Constants.score_result_type.bitch_in_season,
    Constants.score_result_type.disqualified,
    Constants.score_result_type.excused,
    Constants.score_result_type.scratched,
    Constants.score_result_type.conflict,
  ].includes(result.id);

  useEffect(() => {
    let _pResult:any = pResult as any;

    // For Pairs class, ensure result is set on initial mount
    if (isPairsClass && _pResult === undefined && result.id === Constants.score_result_type.non_regular) {
      onChange({
        result_type: result.id,
        total_deductions_only: false,
        time: convertTimeToSeconds(time),
        element_deductions: []
      }, currentShow);
      return;
    }

    if ((_pResult !== undefined && _pResult.id !== result.id) || (totalsOnly !== pTotalsOnly) || (totalAmt !== pTotalAmt) || (deductions !== pDeductions) || (time !== pTime) || (excluded !== pExcluded)) {
      let changePayload:any = {
        result_type: result.id,
        total_deductions_only: totalsOnly,
        time: isNonQualifyingResult ? null : convertTimeToSeconds(time),
        excluded_from_placement: excluded,
      };

      if (isNonQualifyingResult) {
        // For non-qualifying results, clear deductions and time data
        changePayload.total_deductions = null;
        changePayload.element_deductions = [];
      } else if (totalsOnly) {
        changePayload.total_deductions = Number(totalAmt);
        changePayload.element_deductions = [];
      } else {
        changePayload.element_deductions = deductions.filter((d: IDeduction) => !d.disabled).map((d: IDeduction) => {
          return {
            element_name: d.label,
            element_deductions: Number(d.amount)
          }
        });
      }

      onChange(changePayload, currentShow);
    }
  }, [deductions, result, pResult, pDeductions, time, pTime, onChange, currentShow, totalsOnly, pTotalsOnly, totalAmt, pTotalAmt, isNonQualifyingResult, excluded, pExcluded]);

  const reorderExerciseList = (deductions: IDeduction[]) => {
    if (currentShow) {
      let _show:IShow = JSON.parse(JSON.stringify(currentShow));

      let deductionPayload:any = deductions.map((d: IDeduction, index: number) => {
        return {
          sign: d.sign,
          exercise: d.label,
          disabled: d.disabled
        };
      });

      for (let se in _show.show_elements) {
        if (isResultShowElement(_show.show_elements[se])) {
          _show.show_elements[se].exercises = deductionPayload;
          break;
        }
      }

      setCurrentShow(_show);
    }
  }

  return (
    <div className="RallyObedienceResults">
      <div className="row"><Dropdown value={result} onChange={(e: any, value: IOption) => { setResult(value); }} label="Result" options={resultOptions} placeholder="" /></div>
      
      {!isNonQualifyingResult && (
        <>
          <div className="row"><StopWatch time={time} onChange={(value: string) => { setTime(value); }} /></div>
          <div className="totalsOnly">
            <Checkbox id="totalsOnly" onChange={(e) => { setTotalsOnly(!totalsOnly); }} value={totalsOnly} label={"Total Deductions Only"} />
          </div>

          {totalsOnly && <div>
            <Input type="text" onChange={(e: any) => { setTotalAmt(e.target.value); }} label={"Total Deductions"} placeholder="" defaultValue={totalAmt} />
          </div>}
          {loaded && currentShow && !totalsOnly && <DeductionForm
            result={props.result}
            deductions={deductions}
            show={currentShow}
            event={props.event}
            onExerciseChange={() => { setLoaded(false); }}
            onChange={(newDeductions: IDeduction[], reorder?: boolean) => {
              if (reorder === true) {
                reorderExerciseList(newDeductions);
              }

              setDeductions(newDeductions);
            }}
          />}
        </>
      )}
      {props.result.sanctioning_club === Constants.sanctioning_club.UKC && (
        <div className="excludedFromPlacement">
          <Checkbox id="excludedFromPlacement" onChange={(e) => { setExcluded(!excluded); }} value={excluded} label={"Exclude from placements"} />
        </div>
      )}
    </div>
  );
};

export default RallyObedienceResults;